import React, { useEffect } from 'react';
import { FaBook, FaUsers, FaGraduationCap, FaFileAlt, FaChartLine, FaAward, FaShieldAlt, FaGlobeAmericas, FaBolt, FaCheckCircle } from 'react-icons/fa';

const AboutPaperPortal = () => {
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                    }
                });
            },
            { threshold: 0.1 }
        );

        const elements = document.querySelectorAll('.reveal-on-scroll');
        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    return (
        <div className="min-h-screen bg-white">
            <div className="max-w-7xl mx-auto px-4">
                {/* Hero Section */}
                <div className="relative overflow-hidden rounded-3xl mb-20 mt-8">
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900"></div>
                    <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
                        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl"></div>
                    </div>

                    <div className="relative z-10 p-6 sm:p-12 md:p-24">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center text-center lg:text-left">
                            <div className="text-white">
                                <div className="inline-block bg-indigo-500/30 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-indigo-400/50">
                                    <p className="text-xs sm:text-sm font-semibold text-indigo-200">Make in India Initiative</p>
                                </div>
                                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
                                    Paper <span className="bg-gradient-to-r from-indigo-300 to-blue-300 bg-clip-text text-transparent">Portal</span>
                                </h1>
                                <p className="text-base sm:text-xl text-slate-200 mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0 font-light">
                                    Streamlining academic publishing for students, PhD scholars, and reviewers with intelligent features and seamless collaboration.
                                </p>

                            </div>

                            <div className="relative">
                                <div className="bg-gradient-to-br from-indigo-500/20 to-blue-500/20 backdrop-blur-xl rounded-2xl p-8 border border-indigo-400/30 shadow-2xl">
                                    <div className="space-y-5">
                                        <div className="flex items-center gap-4 bg-white/5 backdrop-blur px-5 py-4 rounded-xl border border-white/10 hover:border-indigo-400/50 transition">
                                            <div className="w-10 h-10 bg-indigo-500/30 rounded-lg flex items-center justify-center">
                                                <FaBook className="w-5 h-5 text-indigo-300" />
                                            </div>
                                            <div>
                                                <p className="text-white font-semibold text-sm">10,000+</p>
                                                <p className="text-slate-300 text-xs">Research Papers</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 bg-white/5 backdrop-blur px-5 py-4 rounded-xl border border-white/10 hover:border-indigo-400/50 transition">
                                            <div className="w-10 h-10 bg-blue-500/30 rounded-lg flex items-center justify-center">
                                                <FaUsers className="w-5 h-5 text-blue-300" />
                                            </div>
                                            <div>
                                                <p className="text-white font-semibold text-sm">5,000+</p>
                                                <p className="text-slate-300 text-xs">Active Researchers</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4 bg-white/5 backdrop-blur px-5 py-4 rounded-xl border border-white/10 hover:border-indigo-400/50 transition">
                                            <div className="w-10 h-10 bg-purple-500/30 rounded-lg flex items-center justify-center">
                                                <FaAward className="w-5 h-5 text-purple-300" />
                                            </div>
                                            <div>
                                                <p className="text-white font-semibold text-sm">500+</p>
                                                <p className="text-slate-300 text-xs">Expert Reviewers</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* About Content */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 mb-16 sm:mb-24 items-center">
                    <div className="order-2 lg:order-1 reveal-on-scroll">
                        <div className="w-12 h-1 bg-indigo-600 mb-6 rounded-full"></div>
                        <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">About Paper Portal</h2>
                        <div className="space-y-6">
                            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                                Welcome to <span className="font-semibold text-slate-900">Paper Portal</span>, a premier research-centric ecosystem dedicated to empowering the next generation of academic excellence.
                            </p>
                            <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                                Our platform streamlines the entire research lifecycle—from manuscript submission and expert peer review to secure global dissemination. We believe in transparency, integrity, and efficiency.
                            </p>
                            <p className="text-base sm:text-lg text-slate-700 leading-relaxed font-light italic border-l-4 border-indigo-200 pl-4">
                                Optimized for the "Make in India" initiative, we provide scholars with a robust repository and intelligent tools to maximize their research impact.
                            </p>
                        </div>
                    </div>
                    <div className="order-1 lg:order-2 bg-gradient-to-br from-indigo-100 to-blue-50 rounded-3xl p-8 sm:p-12 h-64 sm:h-80 flex items-center justify-center shadow-lg reveal-on-scroll">
                        <div className="text-center">
                            <div className="inline-block bg-white rounded-2xl p-4 sm:p-6 mb-4 shadow-md">
                                <FaBook className="w-12 h-12 sm:w-20 sm:h-20 text-indigo-600" />
                            </div>
                            <p className="text-slate-900 font-semibold text-lg sm:text-xl">Empowering Academic Excellence</p>
                        </div>
                    </div>
                </div>

                {/* Mission & Vision */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 mb-16 sm:mb-24">
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-10 border border-slate-200 reveal-on-scroll">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                                <FaGraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                            </div>
                            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Our Mission</h3>
                        </div>
                        <p className="text-slate-700 leading-relaxed">
                            To bridge the gap between researchers and global education, making scholarship <span className="font-medium text-indigo-700">accessible, transparent, and efficient</span> for every scholar through innovative digital workflows.
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-10 border border-slate-200 reveal-on-scroll" style={{ transitionDelay: '200ms' }}>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                <FaGlobeAmericas className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                            </div>
                            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">Our Vision</h3>
                        </div>
                        <p className="text-slate-700 leading-relaxed">
                            To become the world's most trusted <span className="font-medium text-blue-700">Academic Bridge</span>, where traditional values meet modern digital convenience to empower researchers everywhere.
                        </p>
                    </div>
                </div>

                {/* What We Do */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 mb-16 sm:mb-24 items-center">
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 sm:p-12 h-64 sm:h-80 flex items-center justify-center shadow-lg reveal-on-scroll">
                        <div className="text-center">
                            <FaUsers className="w-16 h-16 sm:w-24 sm:h-24 text-indigo-300 mx-auto mb-4" />
                            <p className="text-white font-semibold text-base sm:text-lg">Connecting Researchers Worldwide</p>
                        </div>
                    </div>
                    <div className="reveal-on-scroll" style={{ transitionDelay: '200ms' }}>
                        <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">What We Do</h2>
                        <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                            We provide a <span className="font-semibold text-slate-900">vibrant, centralized ecosystem</span> for Young India to express their creativity through high-quality research. From plagiarism detection to global citation management, we bring world-class academic tools together in one convenient place.
                        </p>
                    </div>
                </div>

                {/* Key Features Section */}
                <div className="mb-16 sm:mb-24 relative">
                    <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-12 sm:mb-16 text-center tracking-tight">Key Features</h2>
                    
                    {/* Vertical Connective Line (Mobile Only) */}
                    <div className="absolute left-[31px] top-[100px] bottom-[40px] w-0.5 bg-gradient-to-b from-indigo-500 via-emerald-500 to-transparent md:hidden"></div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 relative">
                        {[
                            { icon: FaCheckCircle, title: "Seamless Registration", desc: "Create account, select institution, and get instant access to your personalized dashboard.", color: "indigo", delay: "delay-100" },
                            { icon: FaChartLine, title: "Intelligent Dashboard", desc: "Role-based interface customized for students, scholars, or reviewers.", color: "blue", delay: "delay-200" },
                            { icon: FaFileAlt, title: "Content Creation", desc: "Write, edit, or upload PDF/Word files with real-time editing.", color: "emerald", delay: "delay-300" },
                            { icon: FaUsers, title: "Expert Reviewers", desc: "Access curated panel with smart expertise matching.", color: "purple", delay: "delay-400" },
                            { icon: FaBolt, title: "Real-Time Collab", desc: "Web and mobile access with live synchronization.", color: "orange", delay: "delay-500" },
                            { icon: FaShieldAlt, title: "Secure Payments", desc: "Integrated payment gateway with instant receipts.", color: "red", delay: "delay-600" }
                        ].map((feature, idx) => (
                            <div 
                                key={`feat-${idx}`} 
                                className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-slate-200 overflow-hidden transition-all duration-300 group z-10 reveal-on-scroll"
                                style={{ transitionDelay: `${(idx % 3) * 150}ms` }}
                            >
                                <div className={`h-24 sm:h-32 bg-gradient-to-br from-${feature.color}-500 to-${feature.color}-600 flex items-center justify-center group-hover:scale-105 transition duration-500`}>
                                    <feature.icon className="w-10 h-10 sm:w-14 sm:h-14 text-white" />
                                </div>
                                <div className="p-5 sm:p-6">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className={`md:hidden w-8 h-8 rounded-full bg-${feature.color}-100 flex items-center justify-center border-2 border-white shadow-sm`}>
                                            <span className={`text-xs font-bold text-${feature.color}-600`}>{idx + 1}</span>
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900">{feature.title}</h3>
                                    </div>
                                    <p className="text-slate-600 text-sm leading-relaxed font-light">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Platform Benefits */}
                <div className="mb-16 sm:mb-24 relative">
                    <h2 className="text-2xl sm:text-4xl font-bold text-slate-900 mb-12 sm:mb-16 text-center tracking-tight">Why Choose Paper Portal</h2>
                    
                    {/* Vertical Flow Line (Mobile Only) */}
                    <div className="absolute left-[31px] top-[100px] bottom-[20px] w-0.5 bg-gradient-to-b from-indigo-500 via-blue-500 to-transparent md:hidden"></div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
                        {[
                            { icon: FaBolt, title: "Time Efficiency", desc: "Streamlined workflows reduce submission and review time significantly.", color: "indigo" },
                            { icon: FaShieldAlt, title: "Secure Platform", desc: "Enterprise-grade security protects your intellectual property and data.", color: "blue" },
                            { icon: FaUsers, title: "Expert Network", desc: "Connect with leading scholars and reviewers in your field.", color: "emerald" },
                            { icon: FaGlobeAmericas, title: "Global Access", desc: "Work from anywhere with cross-platform synchronization.", color: "purple" },
                            { icon: FaAward, title: "Easy-to-Use", desc: "User-focused design with reliable and well-organized resources.", color: "yellow" },
                            { icon: FaBook, title: "Make in India", desc: "Designed for and by Indian academic excellence.", color: "red" }
                        ].map((benefit, idx) => (
                            <div 
                                key={`ben-${idx}`} 
                                className={`bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-8 border border-slate-200 hover:border-${benefit.color}-300 transition-all duration-300 z-10 reveal-on-scroll`}
                                style={{ transitionDelay: `${(idx % 2) * 200}ms` }}
                            >
                                <div className="flex items-start gap-4 sm:gap-6">
                                    <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-${benefit.color}-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-${benefit.color}-200`}>
                                        <benefit.icon className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                                        <p className="text-slate-700 text-sm font-light leading-relaxed">{benefit.desc}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Who Can Benefit */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 rounded-2xl sm:rounded-3xl shadow-lg p-8 sm:p-12 md:p-16 text-white mb-16 sm:mb-24 relative overflow-hidden">
                    <h2 className="text-2xl sm:text-4xl font-bold mb-12 sm:mb-16 text-center tracking-tight reveal-on-scroll">Who Can Benefit</h2>
                    
                    {/* Vertical Flow Line (Mobile Only) */}
                    <div className="absolute left-[31px] sm:left-[47px] top-[140px] bottom-[60px] w-0.5 bg-gradient-to-b from-indigo-500/50 to-transparent md:hidden"></div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 relative">
                        {[
                            { icon: FaGraduationCap, title: "For Students", desc: "Submit and track your research with ease and confidence and get recognized.", color: "indigo" },
                            { icon: FaBook, title: "For Scholars", desc: "Collaborate efficiently with peers and reviewers worldwide.", color: "blue" },
                            { icon: FaUsers, title: "For Reviewers", desc: "Manage reviews seamlessly with powerful tools at your fingertips.", color: "purple" }
                        ].map((item, idx) => (
                            <div key={`ben-${idx}`} className="text-center reveal-on-scroll" style={{ transitionDelay: `${idx * 200}ms` }}>
                                <div className="relative">
                                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-indigo-500/30 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-400/50 relative z-10">
                                        <item.icon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-300" />
                                    </div>
                                    <div className="md:hidden absolute -left-1 top-6 w-4 h-4 rounded-full bg-indigo-500 border-2 border-slate-900 z-20"></div>
                                </div>
                                <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                                <p className="text-slate-300 font-light leading-relaxed max-w-xs mx-auto">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>


            </div>
        </div>
    );
};

export default AboutPaperPortal;
